import { Button, Grid, Group, Modal, Text, TextInput } from '@mantine/core';
import { IconMail, IconPointer, IconSend, IconUser } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import EventSelector from '../../components/EventSelector';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import TriggerEventMessage from '../TriggerEventMessage';

const SendTestMessageModal = ({
  send,
  close,
  error,
  sending,
  show,
  sendingDisabled,
  sendTo: passedSendTo,
  sendToPlaceholder,
  sendToHelperText = '',
  sendToRequired = true,
  showSendTo = true,
  isSendToValid = () => true,
}) => {
  if (!showSendTo) {
    sendToRequired = false;
  }

  const fetch = useFetch();
  const [sendTo, setSendTo] = useState(passedSendTo || '');
  const [impersonateProfileQuery, setImpersonateProfileQuery] = useState(passedSendTo || '');
  const [triggerEventAction, setTriggerEventAction] = useState();
  const [loadError, setLoadError] = useState();
  const { domain } = useSelector((state) => state.domain);
  const [eventActions, setEventActions] = useState();

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/eventActions`)
      .then(({ eventActions }) => setEventActions(eventActions))
      .catch(setLoadError);
  }, [fetch, domain.accountId, domain.id]);

  if (!eventActions) {
    return <Spinner />;
  }

  if (loadError) {
    return;
  }

  return (
    <Modal size="lg" opened={show} onClose={() => close()} title="Test Message">
      <div data-tour="testEmailModal">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(sendTo, impersonateProfileQuery, triggerEventAction, close);
            return false;
          }}
        >
          {loadError ? (
            <ErrorMessage message={loadError.message} />
          ) : (
            <>
              {showSendTo && (
                <Grid mb="sm">
                  <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'top' }}>
                    <label>
                      <IconMail size={14} style={{ marginRight: 4 }} /> Send to
                    </label>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <TextInput
                      placeholder={sendToPlaceholder}
                      name="sendTo"
                      value={sendTo}
                      onChange={(e) => setSendTo(e.target.value)}
                      error={sendTo && !isSendToValid(sendTo) ? 'Value is invalid!' : null}
                    />
                    {sendToHelperText && (
                      <Text size="xs" c="dimmed" mt="xs">
                        <strong>{sendToHelperText}</strong>
                      </Text>
                    )}
                  </Grid.Col>
                </Grid>
              )}

              <Grid mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'top' }}>
                  <label>
                    <IconUser size={14} style={{ marginRight: 4 }} /> Profile to impersonate
                  </label>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <TextInput
                    name="email"
                    placeholder="Type email, phone number (with +countryCode), or profile id"
                    value={impersonateProfileQuery}
                    onChange={(e) => setImpersonateProfileQuery(e.target.value)}
                  />
                  <Text size="xs" c="dimmed" mt="xs">
                    The profile you want to get the data from when sending the message. <br />
                    <br /> For example if you have
                    <br /> - "Send to" - john.doe@example.com <br /> - "Profile to impersonate" - alice@example.com{' '}
                    <br />
                    <br />
                    You use {'{{firstName}}'} in the message you sent, we will send the message to john.doe@example.com
                    with message containing alice@example.com first name - "Alice".
                  </Text>
                </Grid.Col>
              </Grid>
              {eventActions && (
                <Grid mb="sm">
                  <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'top' }}>
                    <label>
                      <IconPointer size={14} style={{ marginRight: 4 }} /> Trigger event
                    </label>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <EventSelector
                      actions={[triggerEventAction]}
                      setActions={([action]) => setTriggerEventAction(action)}
                    />
                    <Text size="xs" c="dimmed" mt="xs">
                      <TriggerEventMessage />
                    </Text>
                  </Grid.Col>
                </Grid>
              )}
            </>
          )}

          {error && <ErrorMessage message={error.message} />}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => close()}>
              Close
            </Button>
            <Button
              disabled={sending || (sendToRequired && !sendTo) || sendingDisabled || !isSendToValid(sendTo)}
              type="submit"
              data-tour="testEmailModalSendBtn"
              leftSection={<IconSend size={16} />}
            >
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </Group>
        </form>
      </div>
    </Modal>
  );
};

export default SendTestMessageModal;
